// Staging of operator file uploads for the assistant chat composer.
//
// Same two-step shape as the document library (src/venus/.../FileUploadProvider):
// a mutation creates the row and hands back a presigned GCS URL, the browser PUTs
// the bytes straight to GCS, then a second mutation confirms and kicks off
// captioning / text extraction. Nothing streams through Jupiter.
//
// Lives in frontend-common rather than Venus because Neptune has no upload
// machinery of its own, and both surfaces must offer the same feature.

import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";
import type { PendingChatAttachment } from "../types/assistant";
import { formatFileSize, uploadFileToGcs } from "../lib/gcsUpload";

// ── Limits, mirrored from jupiter.services.assistant_attachment ─────────
// The backend re-checks every one of these; these copies exist so the operator
// gets an immediate, German error instead of a round trip.

export const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const CHAT_ATTACHMENT_MAX_PER_MESSAGE = 5;

/** Accepted MIME types. No spreadsheets: an extracted table is poor answer context. */
export const CHAT_ATTACHMENT_CONTENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff",
] as const;

/**
 * `accept` for the file picker. Extensions are listed alongside the MIME types
 * because browsers report .doc inconsistently, and image/tiff is often missing
 * from the OS type map entirely.
 */
export const CHAT_ATTACHMENT_ACCEPT = [
  ...CHAT_ATTACHMENT_CONTENT_TYPES,
  ".pdf",
  ".doc",
  ".docx",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".tif",
  ".tiff",
].join(",");

// ── GraphQL ────────────────────────────────────────────────────────────

const ATTACHMENT_ADD_MUTATION = gql`
  mutation AssistantConversationAttachmentAdd(
    $fileName: String!
    $fileSize: Int64!
    $contentType: String!
    $conversationId: UUID
  ) {
    assistantConversationAttachmentAdd(
      fileName: $fileName
      fileSize: $fileSize
      contentType: $contentType
      conversationId: $conversationId
    ) {
      id
      presignedUrl
    }
  }
`;

const ATTACHMENT_UPLOAD_COMPLETED_MUTATION = gql`
  mutation AssistantConversationAttachmentUploadCompleted(
    $attachmentId: UUID!
    $success: Boolean!
  ) {
    assistantConversationAttachmentUploadCompleted(
      attachmentId: $attachmentId
      success: $success
    ) {
      id
      name
      contentType
      size
      isProcessed
      hasExtractedText
      useInAnswer
      textOnly
    }
  }
`;

const ATTACHMENT_REMOVE_MUTATION = gql`
  mutation AssistantConversationAttachmentRemove($attachmentId: UUID!) {
    assistantConversationAttachmentRemove(attachmentId: $attachmentId)
  }
`;

const STAGED_ATTACHMENTS_QUERY = gql`
  query AssistantStagedAttachments($conversationId: UUID!) {
    assistantStagedAttachments(conversationId: $conversationId) {
      id
      name
      contentType
      size
      isProcessed
      hasExtractedText
      useInAnswer
      textOnly
    }
  }
`;

/**
 * Reject a file locally with the reason, or null when it is acceptable. The
 * type check falls back to the extension because browsers hand back an empty
 * `type` for some Office and TIFF files.
 */
function rejectReason(file: File): string | null {
  const typeOk =
    (CHAT_ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(file.type) ||
    /\.(pdf|docx?|png|jpe?g|gif|webp|bmp|tiff?)$/i.test(file.name);
  if (!typeOk) {
    return "Nicht unterstützter Dateityp. Erlaubt sind PDF, Bilder und Word-Dateien.";
  }
  if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
    return `Datei zu groß (${formatFileSize(file.size)}). Maximal ${formatFileSize(
      CHAT_ATTACHMENT_MAX_BYTES,
    )} pro Datei.`;
  }
  if (file.size < 16) return "Die Datei ist leer.";
  return null;
}

// ── Hook ───────────────────────────────────────────────────────────────

interface UseChatAttachmentsOptions {
  /**
   * Conversation the uploads belong to, or null for the first message of a chat
   * (composed before the conversation exists). Staged rows are bound to the
   * conversation when the turn is sent either way.
   */
  conversationId: string | null;
}

interface UseChatAttachmentsReturn {
  /** Staged files, in the order they were added. */
  attachments: PendingChatAttachment[];
  /** True while any upload is still in flight — the composer blocks send until it clears. */
  isUploading: boolean;
  /** Server ids of the staged files that finished uploading, for the send mutation. */
  readyIds: string[];
  /** Add files from a picker, a drop, or a paste. Over-cap and invalid files are rejected. */
  addFiles: (files: File[] | FileList) => void;
  /** Remove a staged file (aborts an in-flight upload and deletes the server row). */
  removeAttachment: (key: string) => void;
  /** Drop all staged files from local state. Call after a successful send. */
  clearStaged: () => void;
}

export function useChatAttachments({
  conversationId,
}: UseChatAttachmentsOptions): UseChatAttachmentsReturn {
  const client = useApolloClient();
  const [attachments, setAttachments] = useState<PendingChatAttachment[]>([]);
  const abortRefs = useRef<Record<string, AbortController>>({});
  // Read inside addFiles so the cap check sees the current list without making
  // the callback depend on (and be recreated by) every progress tick.
  const attachmentsRef = useRef<PendingChatAttachment[]>([]);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Adopt whatever is already staged server-side. This is what makes a reload
  // non-destructive: the row is the source of truth, so a chip is never shown
  // for a file that is not actually in GCS.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await client.query<{
          assistantStagedAttachments: {
            id: string;
            name: string;
            contentType: string;
            size: string;
          }[];
        }>({
          query: STAGED_ATTACHMENTS_QUERY,
          variables: { conversationId },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const staged = result.data?.assistantStagedAttachments ?? [];
        if (staged.length === 0) return;
        setAttachments((prev) => {
          const known = new Set(prev.map((a) => a.id).filter(Boolean));
          const adopted = staged
            .filter((s) => !known.has(s.id))
            .map<PendingChatAttachment>((s) => ({
              id: s.id,
              key: s.id,
              name: s.name,
              contentType: s.contentType,
              size: Number(s.size),
              progress: 100,
              status: "ready",
            }));
          return adopted.length > 0 ? [...adopted, ...prev] : prev;
        });
      } catch (error) {
        // Non-fatal: the operator can re-upload. Never block the composer on this.
        console.error("Failed to load staged chat attachments:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, client]);

  // Abort in-flight uploads on unmount so a closed panel does not keep pushing bytes.
  useEffect(
    () => () => {
      for (const controller of Object.values(abortRefs.current)) controller.abort();
      abortRefs.current = {};
    },
    [],
  );

  const uploadOne = useCallback(
    async (file: File, key: string) => {
      const controller = new AbortController();
      abortRefs.current[key] = controller;
      try {
        const added = await client.mutate<{
          assistantConversationAttachmentAdd: { id: string; presignedUrl: string | null };
        }>({
          mutation: ATTACHMENT_ADD_MUTATION,
          variables: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || "application/octet-stream",
            conversationId,
          },
        });
        const created = added.data?.assistantConversationAttachmentAdd;
        if (!created?.presignedUrl) throw new Error("Kein Upload-Ziel erhalten.");

        setAttachments((prev) =>
          prev.map((a) => (a.key === key ? { ...a, id: created.id } : a)),
        );

        await uploadFileToGcs(
          created.presignedUrl,
          file,
          (uploadedBytes) =>
            setAttachments((prev) =>
              prev.map((a) =>
                a.key === key
                  ? {
                      ...a,
                      progress: file.size
                        ? Math.round((uploadedBytes / file.size) * 100)
                        : 0,
                    }
                  : a,
              ),
            ),
          controller.signal,
        );

        await client.mutate({
          mutation: ATTACHMENT_UPLOAD_COMPLETED_MUTATION,
          variables: { attachmentId: created.id, success: true },
        });

        setAttachments((prev) =>
          prev.map((a) =>
            a.key === key ? { ...a, progress: 100, status: "ready" as const } : a,
          ),
        );
      } catch (error) {
        const aborted = controller.signal.aborted;
        const message = error instanceof Error ? error.message : String(error);
        // An abort is the operator removing the chip; removeAttachment already
        // dropped the row and told the server, so there is nothing to report.
        if (!aborted) {
          setAttachments((prev) =>
            prev.map((a) =>
              a.key === key ? { ...a, status: "failed" as const, error: message } : a,
            ),
          );
          toast.error(`"${file.name}" konnte nicht hochgeladen werden`, {
            description: message,
          });
        }
      } finally {
        delete abortRefs.current[key];
      }
    },
    [client, conversationId],
  );

  const addFiles = useCallback(
    (input: File[] | FileList) => {
      const files = Array.from(input);
      if (files.length === 0) return;

      const current = attachmentsRef.current.length;
      const room = CHAT_ATTACHMENT_MAX_PER_MESSAGE - current;
      if (room <= 0) {
        toast.error(
          `Höchstens ${CHAT_ATTACHMENT_MAX_PER_MESSAGE} Anhänge pro Nachricht.`,
        );
        return;
      }
      if (files.length > room) {
        toast.error(
          `Höchstens ${CHAT_ATTACHMENT_MAX_PER_MESSAGE} Anhänge pro Nachricht. ` +
            `${files.length - room} Datei(en) wurden nicht hinzugefügt.`,
        );
      }

      for (const file of files.slice(0, room)) {
        const reason = rejectReason(file);
        if (reason) {
          toast.error(`"${file.name}" wurde nicht hinzugefügt`, { description: reason });
          continue;
        }
        const key = `chatatt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setAttachments((prev) => [
          ...prev,
          {
            id: null,
            key,
            name: file.name,
            contentType: file.type || "application/octet-stream",
            size: file.size,
            progress: 0,
            status: "uploading",
          },
        ]);
        void uploadOne(file, key);
      }
    },
    [uploadOne],
  );

  const removeAttachment = useCallback(
    (key: string) => {
      const row = attachmentsRef.current.find((a) => a.key === key);
      setAttachments((prev) => prev.filter((a) => a.key !== key));
      abortRefs.current[key]?.abort();
      if (row?.id) {
        void client
          .mutate({
            mutation: ATTACHMENT_REMOVE_MUTATION,
            variables: { attachmentId: row.id },
          })
          .catch((error: unknown) => {
            // The chip is already gone locally; a stray unbound row is harmless.
            console.error("Failed to remove chat attachment:", error);
          });
      }
    },
    [client],
  );

  const clearStaged = useCallback(() => {
    setAttachments([]);
    abortRefs.current = {};
  }, []);

  return {
    attachments,
    isUploading: attachments.some((a) => a.status === "uploading"),
    readyIds: attachments
      .filter((a) => a.status === "ready" && a.id)
      .map((a) => a.id as string),
    addFiles,
    removeAttachment,
    clearStaged,
  };
}
